import React, { useState, useRef, useEffect } from "react";
import LocalizedLink from "@/components/LocalizedLink";
import { useTranslation } from "react-i18next";
import useScrollAnimation from "@/hooks/useScrollAnimation";
import { trackEvent } from "@/utils/analytics";
import { PROJECTS } from "@/data/projects";
import FeatureCardOverlay from "@/components/FeatureCardOverlay/FeatureCardOverlay";
import "./PortfolioSection.scss";

const PrevIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M13 8H3M7 4L3 8l4 4" />
  </svg>
);

const NextIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M3 8h10M9 4l4 4-4 4" />
  </svg>
);

const ExternalIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M7 3H3v10h10V9M10 2h4v4M14 2L8 8" />
  </svg>
);

const ArrowIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M3 8h10M9 4l4 4-4 4" />
  </svg>
);

const PortfolioSection: React.FC = () => {
  const { t } = useTranslation();
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });
  const [active, setActive] = useState(0);
  const tabsRef = useRef<HTMLDivElement>(null);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const el = tabsRef.current;
    if (!el) return;
    const check = () => setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    check();
    el.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check, { passive: true });
    return () => {
      el.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    };
  }, []);

  const scrollTabsRight = () => {
    tabsRef.current?.scrollBy({ left: 120, behavior: "smooth" });
  };

  const p = PROJECTS[active];

  const prev = () => setActive((a) => (a - 1 + PROJECTS.length) % PROJECTS.length);
  const next = () => setActive((a) => (a + 1) % PROJECTS.length);

  return (
    <section className="portfolio-section" ref={ref}>
      <div id="portfolio-section" className="container">
        <div className={`sec-head row reveal${isVisible ? " in" : ""}`}>
          <div>
            <span className="sec-label">{t("portfolio.label")}</span>
            <h2 className="sec-title">
              {(() => {
                const title = t("portfolio.title");
                const hl = t("portfolio.titleHighlight");
                const [before, after = ""] = title.split(hl);
                return <>{before}<em>{hl}</em>{after}</>;
              })()}
            </h2>
          </div>
          <div className="port-tabs-wrap">
            <div className="port-tabs" role="tablist" ref={tabsRef}>
              {PROJECTS.map((pr, i) => (
                <button
                  key={pr.key}
                  className={`port-tab${active === i ? " active" : ""}`}
                  onClick={() => setActive(i)}
                  role="tab"
                  aria-selected={active === i}
                >
                  {pr.name}
                </button>
              ))}
            </div>
            {canScrollRight && (
              <button
                className="port-tabs-arrow"
                onClick={scrollTabsRight}
                aria-label={t("portfolio.scrollMoreAriaLabel")}
              >
                <NextIcon />
              </button>
            )}
          </div>
        </div>

        <div className="port-stage">
          {/* Frame links to the case study */}
          <LocalizedLink
            to={`/work/${p.slug}`}
            className="port-frame"
            aria-label={`${t("portfolio.viewCaseStudy")} ${p.name}`}
            onClick={() => trackEvent("case_study_open", { project: p.key })}
          >
            {PROJECTS.map((pr, i) => (
              <picture
                key={pr.key}
                className={`port-frame-img${active === i ? " active" : ""}`}
              >
                <source
                  type="image/webp"
                  srcSet={`${pr.img.webp} 640w, ${pr.img.webp2x} 1280w`}
                  sizes="(max-width: 640px) 640px, 1280px"
                />
                <img
                  src={pr.img.fallback}
                  srcSet={`${pr.img.fallback} 1280w`}
                  sizes="(max-width: 640px) 640px, 1280px"
                  alt={pr.name}
                  width={pr.img.width}
                  height={pr.img.height}
                  loading="lazy"
                  decoding="async"
                  aria-hidden={active !== i}
                />
              </picture>
            ))}
            <FeatureCardOverlay
              name={p.name}
              tagline={t(`caseStudies.${p.slug}.tagline`)}
              tags={p.tags}
            />
            {p.featured && (
              <div className="port-badge">
                <span className="port-badge-dot" aria-hidden="true" />
                {t("portfolio.featuredLabel")}
              </div>
            )}
          </LocalizedLink>

          <div className={`port-meta reveal${isVisible ? " in" : ""}`} key={active}>
            {/* The card carries the name/tagline/tags; meta is detail + actions */}
            <p className="port-desc">{t(`portfolio.${p.key}.description`)}</p>

            <div className="port-actions">
              {/* Primary: view case study */}
              <LocalizedLink
                to={`/work/${p.slug}`}
                className="port-action-btn port-action-btn--primary"
                onClick={() => trackEvent("case_study_open", { project: p.key })}
              >
                {t("portfolio.viewCaseStudy")} <ArrowIcon />
              </LocalizedLink>
              {/* Secondary: visit live */}
              <a
                href={p.liveUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="port-action-btn port-action-btn--secondary"
                aria-label={`${t("portfolio.visitLiveLabel")} ${p.name}`}
                onClick={() => trackEvent("portfolio_open", { project: p.key })}
              >
                {t("portfolio.visitLiveLabel")} <ExternalIcon />
              </a>
            </div>

            <div className="port-nav">
              <button className="port-nav-btn" onClick={prev} aria-label={t("portfolio.prevProjectAriaLabel")}>
                <PrevIcon />
              </button>
              <button className="port-nav-btn" onClick={next} aria-label={t("portfolio.nextProjectAriaLabel")}>
                <NextIcon />
              </button>
              <span className="port-count">
                {String(active + 1).padStart(2, "0")} / {String(PROJECTS.length).padStart(2, "0")}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PortfolioSection;
